import { H, S } from '@infotech-soe/erp-ui-kit';
import {
  DEFAULT_DATASET_FIELDS,
  TTreeState,
} from '@infotech-soe/merps/types/Queries';
import {
  CategoryFieldName,
  CategoryName,
  DisplayModeInParamName,
  ICategoryFieldParsed,
  ICategoryParsed,
  IDisplayModeParsed,
  IInParamsValues,
  ILocalizedValue,
  IPlainCategoryFieldParsed,
  IViewFieldParsed,
  IViewFieldWithDomainParsed,
  IViewFieldWithFieldParsed,
  IViewParsed,
  TDatabaseId,
  TDatabaseRecord,
  TDatasetName,
  TDatasetTotalsRuleServer,
  tg,
  THeaderResult,
  TSortQueryDirEnum,
  TSortQueryParams,
  TViewFieldName,
  TViewName,
} from '@infotech-soe/metaschema';
import pick from 'lodash/pick';

import { StoredIntlSource } from '../../../infrastructure/intl/StoredIntlSource';
import IApiProvider, {
  DatasetParamValues,
} from '../../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import CachedSchemaRepository from '../../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../../infrastructure/schemas/storage/ISchemaRepository';
import { NamedDomain } from '../../../infrastructure/schemas/storage/NamedDomain';
import {
  DatasetConfig,
  TOTALS_FORMULA_ATTACHMENT,
  TRecordViewMap,
} from '../../../models/user-config/DisplayModeConfig';
import CategoryConditionsService from '../../category/CategoryConditionsService';
import ICategoryConditionsService from '../../category/ICategoryConditionsService';
import CategoryViewModel from '../../category/view-model/CategoryViewModel';
import CategoryViewModelSource from '../../category/view-model/CategoryViewModelSource';
import DatasetViewModel from '../../category/view-model/DatasetViewModel';
import ContextService from '../../context/ContextService';
import IContextService from '../../context/IContextService';
import IIntlService from '../../intl/IIntlService';
import IntlService from '../../intl/IntlService';
import UniqueId from '../../uid/UniqueId';
import { DisplayModeConditions } from '../../user-config/IUserConfigService';
import { TotalsConfigSanitizer } from '../TotalsConfigSanitizer';
import { CreateDatasetResult } from '../view-model/CreateDatasetResult';
import { DatasetCurrentState } from '../view-model/DatasetCurrentState';
import {
  DatasetColumnFormat,
  DatasetGridColumn,
} from '../view-model/DatasetGridColumn';
import { DatasetGridRow } from '../view-model/DatasetGridRow';
import { UpdateDatasetResult } from '../view-model/UpdateDatasetResult';

import IDisplayModeDatasetService from './IDisplayModeDatasetService';

export const DEFAULT_ROWS_LIMIT_IN_DATASET = 1_000;
const ERROR_DELIMITER = '::';
const ERROR_MESSAGES_CATEGORY = 'ErrorMessages';

const enum SourceType {
  CATEGORY = 'category',
  VIEW = 'view',
}

interface CategorySourceSchema {
  type: SourceType.CATEGORY;
  schema: ICategoryParsed;
}

interface ViewSourceSchema {
  type: SourceType.VIEW;
  schema: IViewParsed;
}

type SourceSchema = CategorySourceSchema | ViewSourceSchema;

interface NormalizedViewField {
  viewField: CategoryFieldName;
  categoryField: CategoryFieldName | IViewFieldWithDomainParsed;
}

function isSimpleViewFieldDescription(
  val: IViewFieldParsed
): val is IViewFieldWithFieldParsed {
  return (val as IViewFieldWithFieldParsed).field !== undefined;
}

class DisplayModeDatasetServiceSingleton implements IDisplayModeDatasetService {
  private commonResources: StoredIntlSource = {};
  private commonResourcesLoaded = false;
  private errorResources: StoredIntlSource = {};
  private errorResourcesLoaded = false;
  private readonly apiProvider: IApiProvider;
  private readonly schemaRepository: ISchemaRepository;
  private readonly intlService: IIntlService;
  private readonly conditionsService: ICategoryConditionsService;
  private readonly contextService: IContextService;
  constructor() {
    this.apiProvider = new MerpsApiProvider();
    this.schemaRepository = new CachedSchemaRepository();
    this.intlService = new IntlService();
    this.conditionsService = new CategoryConditionsService();
    this.contextService = new ContextService();
  }

  private async getCommonResources(
    categoryName?: CategoryName
  ): Promise<StoredIntlSource> {
    if (!this.commonResourcesLoaded) {
      this.commonResources = {
        ...(await this.intlService.getCommon()),
        ...(categoryName
          ? await this.intlService.getCategoryMessages(categoryName)
          : undefined),
      };
      this.commonResourcesLoaded = true;
    }
    return this.commonResources;
  }

  private async getErrorResources(): Promise<StoredIntlSource> {
    if (!this.errorResourcesLoaded) {
      this.errorResources = await this.intlService.getCategoryMessages(
        ERROR_MESSAGES_CATEGORY
      );
      this.errorResourcesLoaded = true;
    }
    return this.errorResources;
  }

  public async createDataset(
    source: CategoryViewModelSource,
    dataset: DatasetViewModel
  ): Promise<CreateDatasetResult> {
    const displayModeSchema = await this.schemaRepository.getDisplayMode(
      source.category,
      source.displayMode
    );

    const datasetSchema = displayModeSchema.datasets[dataset.name];

    const { projection, editableFields, sort } = datasetSchema;
    const columns = await this.getColumnsForSource(
      datasetSchema.source,
      projection
    );

    for (const column of columns) {
      column.title = await this.intlService.getDisplayModeDatasetColumnTitle(
        source.category,
        source.displayMode,
        dataset.name,
        column.field
      );
    }

    const idField = datasetSchema.context?.id.field ?? 'NRN';

    const category = await this.apiProvider.schemas.getDatasetCategoryName(
      source.category,
      source.displayMode,
      dataset.name
    );

    const defaultSort = sort
      ? Object.entries(sort).map(([field, dir]) => ({
          field,
          dir: dir as TSortQueryDirEnum,
          default: true,
        }))
      : [];

    return {
      columns,
      idField,
      treeField: datasetSchema.treeField,
      source: { category },
      editableFields,
      defaultSort,
    };
  }

  public async updateDataset(
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    datasetId: UniqueId,
    conditions: DisplayModeConditions,
    treeState?: TTreeState,
    sort?: TSortQueryParams[]
  ): Promise<UpdateDatasetResult> {
    const dataset = category.datasets[datasetId];
    const source = category.source;

    const displayModeSchema = await this.schemaRepository.getDisplayMode(
      source.category,
      source.displayMode
    );

    const inParamValues = source.inParams;
    const paramValues = this.getDatasetParamValues(
      displayModeSchema,
      dataset.name,
      category.datasets,
      datasetsState,
      inParamValues
    );

    if (!paramValues) {
      return {
        rows: [],
        rowsFullCount: 0,
      };
    }

    const positionParams: Array<[DisplayModeInParamName, CategoryFieldName]> =
      [];
    if (inParamValues && displayModeSchema.inParams) {
      for (const paramName of Object.keys(inParamValues)) {
        const definition = displayModeSchema.inParams[paramName];
        if (!definition) {
          continue;
        }
        if (definition.kind === 'pos') {
          const [datasetName, fieldName] = definition.field.split('.');
          if (datasetName === dataset.name) {
            positionParams.push([paramName, fieldName]);
          }
        }
      }
    }

    const conds = conditions[dataset.name]
      ? { ...conditions[dataset.name] }
      : this.conditionsService.defaultCondition();

    const { preset, currentPreset } = datasetsState[datasetId].config;
    const {
      totals: totalsConfig,
      hasRowsLimitInDataset,
      valueOfRowsLimitInDataset,
      caseSensitive = true,
    } = preset[currentPreset];

    conds.queryOptions = { caseSensitive };

    const newServerTotalsConfig: TDatasetTotalsRuleServer[] = [];
    if (totalsConfig) {
      Object.entries(totalsConfig).forEach(([field, rule]) => {
        newServerTotalsConfig.push(
          ...(rule
            ?.filter(
              ([, , attachment]) => attachment === TOTALS_FORMULA_ATTACHMENT.ALL
            )
            .map<TDatasetTotalsRuleServer>(([id, formula]) => [
              field,
              id,
              formula,
            ]) ?? [])
        );
      });
    }

    const { rows, metaData } = await this.apiProvider.displayModes.getDataset(
      source.category,
      source.displayMode,
      dataset.name,
      paramValues,
      category.processId,
      conds,
      treeState,
      sort,
      dataset.parent ? undefined : source.inParams?.linksGraph,
      hasRowsLimitInDataset
        ? (valueOfRowsLimitInDataset ?? DEFAULT_ROWS_LIMIT_IN_DATASET)
        : undefined,
      newServerTotalsConfig.length ? newServerTotalsConfig : undefined
    );

    const defaultFields = [DEFAULT_DATASET_FIELDS.COUNT];

    const nonDatasetFields = [
      ...(newServerTotalsConfig.map(([, id]) => id) ?? []),
      ...defaultFields,
    ];

    const auxiliaryFieldsIndexes: Array<[string, number]> = [];

    const datasetFieldsIndexes =
      metaData
        .map(({ name }, index) => {
          if (!nonDatasetFields.includes(name)) {
            return index;
          }
          auxiliaryFieldsIndexes.push([name, index]);
        })
        .filter((val): val is number => val !== undefined) ?? [];

    const auxiliaryFieldsValues = auxiliaryFieldsIndexes.reduce<
      Record<string, number>
    >(
      (acc, [fieldName, index]) => ({
        ...acc,
        [fieldName]: (rows[0]?.[index] as number | undefined) ?? 0,
      }),
      {}
    );

    const { [DEFAULT_DATASET_FIELDS.COUNT]: rowsFullCount, ...totals } =
      auxiliaryFieldsValues;

    const resultNew =
      rows.map(row => {
        const newRow: Record<string, string | number | null> = {};
        for (const index of datasetFieldsIndexes) {
          newRow[metaData[index].name] = row[index];
        }
        return newRow;
      }) ?? [];

    return {
      rows: resultNew,
      rowsFullCount,
      totals,
      activeRowId: this.findPosition(
        inParamValues,
        resultNew,
        positionParams,
        datasetsState[datasetId]
      ),
      header: await this.getHeader(category, datasetsState, datasetId),
    };
  }

  private getDatasetParamValues(
    displayModeSchema: IDisplayModeParsed,
    datasetName: TDatasetName,
    datasets: Record<UniqueId, DatasetViewModel>,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    inParams?: IInParamsValues
  ): DatasetParamValues | null {
    const datasetSchema = displayModeSchema.datasets[datasetName];
    const paramValues: DatasetParamValues = {};

    if (!datasetSchema.params) {
      return paramValues;
    }

    for (const [paramName, paramDef] of Object.entries(datasetSchema.params)) {
      if (tg.hasProperty(paramDef, 'value')) {
        if (tg.isSomething(paramDef.value)) {
          paramValues[paramName] = paramDef.value;
        } else if (paramDef.nullable) {
          paramValues[paramName] = null;
        }
      }
      if (tg.hasProperty<'refField'>(paramDef, 'refField')) {
        const [refDataset, refField] = paramDef.refField.split('.');

        const refDatasetId = this.findDatasetIdByName(datasets, refDataset);

        const refDatasetRow = this.getDatasetActiveRow(
          datasetsState[refDatasetId]
        );

        if (!refDatasetRow) {
          return null;
        }

        paramValues[paramName] = refDatasetRow[refField];
      }
      if (
        tg.hasProperty<'param'>(paramDef, 'param') &&
        tg.hasProperty(inParams, paramDef.param)
      ) {
        const inParamsValue = inParams[paramDef.param];
        if (tg.isSomething(inParamsValue) && !tg.isEmptyString(inParamsValue)) {
          paramValues[paramName] = inParamsValue;
        } else if (paramDef.nullable) {
          paramValues[paramName] = tg.isEmptyString(inParamsValue)
            ? null
            : inParamsValue;
        }
      }
    }

    return paramValues;
  }

  private findDatasetIdByName(
    datasets: Record<UniqueId, DatasetViewModel>,
    datasetName: UniqueId
  ): UniqueId {
    for (const datasetId of Object.keys(datasets)) {
      const dataset = datasets[datasetId];

      if (dataset.name === datasetName) {
        return datasetId;
      }
    }

    throw new Error('Dataset not found: ' + datasetName);
  }

  private getDatasetActiveRow(
    dataset: DatasetCurrentState
  ): DatasetGridRow | null {
    const activeId = dataset.activeRowId;

    if (activeId === null) {
      return null;
    }

    for (const row of dataset.rows) {
      if (row[dataset.rowIdField] === activeId) {
        return row;
      }
    }

    return null;
  }

  private async getColumnsForSource(
    source: CategoryName | TViewName,
    projection: ILocalizedValue[]
  ): Promise<DatasetGridColumn[]> {
    const sourceSchema = await this.getSourceSchema(source);

    if (sourceSchema.type === SourceType.VIEW) {
      return this.getViewColumns(sourceSchema.schema, projection);
    }

    return this.getCategoryColumns(sourceSchema.schema, projection, source);
  }

  private async getSourceSchema(
    source: CategoryName | TViewName
  ): Promise<SourceSchema> {
    try {
      return {
        type: SourceType.VIEW,
        schema: await this.schemaRepository.getView(source),
      };
    } catch {
      return {
        type: SourceType.CATEGORY,
        schema: await this.schemaRepository.getCategory(source),
      };
    }
  }

  private async getCategoryColumns(
    schema: ICategoryParsed,
    projection: ILocalizedValue[],
    categoryName?: CategoryName
  ): Promise<DatasetGridColumn[]> {
    const columns: DatasetGridColumn[] = [];

    for (const field of projection) {
      const { domain } = schema[field.value] as IPlainCategoryFieldParsed;

      if (!domain) {
        continue;
      }

      const namedDomain = await this.schemaRepository.getDomain(domain);
      const format = this.getDomainFormat(namedDomain);

      if (!format) {
        continue;
      }

      const name = field.value;

      const column: DatasetGridColumn = {
        field: name,
        format,
        domain: namedDomain,
        title: name,
      };

      if (field.formatter) {
        column.formatter = field.formatter;
      } else if (tg.isEnumDomain(namedDomain)) {
        const localizations = new Map();
        for (const value of namedDomain.values) {
          localizations.set(
            value,
            await this.intlService.getDomainEnumValue(namedDomain.name, value)
          );
        }
        column.formatter = value => localizations.get(value);
      } else if (field.translatable) {
        if (tg.isErrorDomain(namedDomain)) {
          const translates = await this.getErrorResources();
          column.formatter = (value: string) => {
            if (value?.includes(ERROR_DELIMITER)) {
              const [message, ...subst] = value.split(ERROR_DELIMITER, 30);
              const errCode = message.split(': ')?.pop();
              if (!errCode) {
                return value;
              }
              return subst.reduce(
                (text, sub, i) => text.replace(`{s${i || ''}}`, sub),
                translates[errCode] ?? value
              );
            }
            return value;
          };
        } else {
          const translates = await this.getCommonResources(categoryName);
          column.formatter = value => translates[value] || value;
        }
      }

      columns.push(column);
    }

    return columns;
  }

  // TODO: handle recursive views
  private async getViewColumns(
    schema: IViewParsed,
    projection: ILocalizedValue[]
  ): Promise<DatasetGridColumn[]> {
    const fieldNames = projection;
    const category = await this.schemaRepository.getCategory(schema.Base);
    const artificialCategory: ICategoryParsed = {};

    const processedFieldNames = fieldNames.map(field => field.value);

    for (const fieldPair of this.normalizeViewFields(schema)) {
      if (!processedFieldNames.includes(fieldPair.viewField)) {
        continue;
      }

      if (typeof fieldPair.categoryField === 'object') {
        artificialCategory[fieldPair.viewField] = fieldPair.categoryField;
        continue;
      }

      const targetPath = fieldPair.categoryField.split('.');

      if (targetPath.length === 1) {
        artificialCategory[fieldPair.viewField] =
          category[fieldPair.categoryField];
      } else {
        artificialCategory[fieldPair.viewField] =
          (await this.schemaRepository.getField(
            schema.Base,
            fieldPair.categoryField
          )) as ICategoryFieldParsed;
      }
    }

    return this.getCategoryColumns(artificialCategory, fieldNames, schema.Base);
  }

  private normalizeViewFields(schema: IViewParsed): NormalizedViewField[] {
    const newNormalizedFields = [];
    for (const [viewField, value] of Object.entries(schema.Fields)) {
      const categoryField = isSimpleViewFieldDescription(value)
        ? value.field
        : value;
      newNormalizedFields.push({
        viewField,
        categoryField,
      });
    }

    return newNormalizedFields;
  }

  private getDomainFormat(domain: NamedDomain): DatasetColumnFormat | null {
    if (domain.type === 'enum') {
      return DatasetColumnFormat.STRING;
    }

    switch (domain.type) {
      case 'number':
        return DatasetColumnFormat.NUMBER;

      case 'string':
        return DatasetColumnFormat.STRING;

      case 'object':
        if (domain.class === 'Date') {
          if (domain.format && domain.format.includes(S)) {
            return DatasetColumnFormat.DATE_TIME_SEC;
          } else if (domain.format && domain.format.includes(H)) {
            return DatasetColumnFormat.DATE_TIME;
          } else {
            return DatasetColumnFormat.DATE;
          }
        }
    }

    return null;
  }

  private findPosition(
    paramValues: IInParamsValues | undefined,
    data: TDatabaseRecord[],
    positionParams: Array<[DisplayModeInParamName, CategoryFieldName]>,
    datasetState: DatasetCurrentState
  ): TDatabaseId | undefined {
    if (!data.length) {
      return undefined;
    }
    const { activeRowId, rowIdField } = datasetState;
    if (activeRowId) {
      const found = data.find(row => row[rowIdField] === activeRowId);
      if (found) {
        return found[rowIdField];
      }
    }
    if (!paramValues) {
      return data[0][rowIdField];
    }
    const filters: Array<[string, any]> = [];
    for (const param of positionParams) {
      const [name, datasetField] = param;
      const value = paramValues[name];
      if (value) {
        filters.push([
          datasetField,
          value instanceof Date ? value.toISOString() : value,
        ]);
      }
    }
    if (!filters.length) {
      return data[0][rowIdField];
    }
    const foundRow = data.find(item =>
      filters.every(([field, paramValue]) => item[field] === paramValue)
    );
    return foundRow ? foundRow[rowIdField] : data[0][rowIdField];
  }

  public async getHeader(
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    datasetId: UniqueId
  ): Promise<string | undefined> {
    const dataset = category.datasets[datasetId];
    const source = category.source;
    const displayModeSchema = await this.schemaRepository.getDisplayMode(
      source.category,
      source.displayMode
    );
    const headerRoutine =
      displayModeSchema.datasets[dataset.name].headerRoutine;

    if (headerRoutine) {
      const context = await this.contextService.getContext(
        category,
        datasetsState,
        datasetId
      );
      const paramValues = this.getDatasetParamValues(
        displayModeSchema,
        dataset.name,
        category.datasets,
        datasetsState,
        source.inParams
      );
      const { label, datasetTitle, substitutions } =
        (await this.apiProvider.actions.executeAction(
          headerRoutine,
          context.toServerContext(),
          { params: paramValues }
        )) as THeaderResult;

      return (
        (datasetTitle
          ? await this.intlService.getCategoryMessage(
              source.category,
              datasetTitle,
              substitutions
            )
          : dataset.title) +
        ': ' +
        (await this.intlService.getCategoryMessage(
          source.category,
          label,
          substitutions
        ))
      );
    }
    return undefined;
  }

  public async getRecordViewMap(
    category: CategoryViewModel,
    datasetId: UniqueId
  ): Promise<TRecordViewMap | undefined> {
    const dataset = category.datasets[datasetId];
    const source = category.source;
    const displayModeSchema = await this.schemaRepository.getDisplayMode(
      source.category,
      source.displayMode
    );
    const datasetSchema = displayModeSchema.datasets[dataset.name];
    const { recordViews } = datasetSchema;

    if (recordViews && Object.keys(recordViews).length) {
      const recordViewMap: TRecordViewMap = {};
      for (const [name, recordView] of Object.entries(recordViews)) {
        recordViewMap[name] = {
          TemplateCode: recordView.TemplateCode,
          getDataRoutine: recordView.getDataRoutine,
          label: await this.intlService.getCategoryFieldTitle(
            source.category,
            recordView.resId ?? name
          ),
          width: recordView.width,
        };
      }
      return recordViewMap;
    }
    return undefined;
  }

  private getSchemaDatasetFields = async (
    { source, datasets }: CategoryViewModel,
    datasetId: UniqueId
  ): Promise<TViewFieldName[] | undefined> =>
    this.schemaRepository
      .getDisplayMode(source.category, source.displayMode)
      .then(dm =>
        this.schemaRepository.getView(
          dm.datasets[datasets[datasetId].name].source
        )
      )
      .then(view => Object.keys(view.Fields));

  private filterConfigPresetByFields = (
    presets: DatasetConfig['preset'],
    fields: TViewFieldName[]
  ): DatasetConfig['preset'] =>
    Object.fromEntries(
      Object.entries(presets).map(([presetName, preset]) => [
        presetName,
        {
          ...preset,
          rules: pick(preset.rules, fields),
          ...(preset.totals && {
            totals: new TotalsConfigSanitizer(
              preset.totals,
              fields
            ).getCleanedConfig(),
          }),
        },
      ])
    );

  public sanitizeDatasetConfig = async (
    category: CategoryViewModel,
    datasetId: UniqueId,
    config: DatasetConfig,
    defaultSort: TSortQueryParams[]
  ): Promise<DatasetConfig> => {
    const schemaFields = await this.getSchemaDatasetFields(category, datasetId);
    if (!schemaFields) return config;
    return {
      ...config,
      columnsWidth: config.columnsWidth
        ? Object.entries(config.columnsWidth)
            .filter(([field]) => schemaFields.includes(field))
            .reduce((acc, [field, value]) => ({ ...acc, [field]: value }), {})
        : {},
      columnsOrder:
        config.columnsOrder?.filter(field => schemaFields.includes(field)) ??
        [],
      preset: config.preset
        ? this.filterConfigPresetByFields({ ...config.preset }, schemaFields)
        : {},
      sort: defaultSort.concat(
        config.sort?.filter(
          ({ field, default: isDefault }) =>
            schemaFields.includes(field) &&
            !isDefault &&
            !defaultSort.some(
              ({ field: defaultField }) => defaultField === field
            )
        ) ?? []
      ),
    };
  };
}

const DisplayModeDatasetService = (() => {
  let instance: IDisplayModeDatasetService;
  return new Proxy(DisplayModeDatasetServiceSingleton, {
    construct(
      Target: typeof DisplayModeDatasetServiceSingleton
    ): IDisplayModeDatasetService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default DisplayModeDatasetService;
